"use client";

import * as React from "react";
import { useEffect, useRef, useCallback, useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  RowData,
  SortingState,
  VisibilityState,
  Table as TableType,
  Row,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useTranslation } from "react-i18next";

import { cn } from "~/../lib/utils";
import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import type { ReactNode } from "react";
import type { TableKpi, TableSelection } from "~/types/ui";

declare module "@tanstack/react-table" {
  interface TableMeta<TData extends RowData> {
    // Last checkbox-selected row id, used to resolve shift-click range selection.
    selectionAnchorRef?: React.MutableRefObject<string | null>;
  }
}

export interface TableProps {
  tableReady?: (table: TableType<any>) => void;
  data: any[];
  columns: ColumnDef<any>[];
  pageSizes?: number[];
  kpis?: TableKpi[];
  searchbar?: boolean;
  searchValue?: string | null;
  setSearchValue?: (value: string) => void;
  selectable?: boolean;
  expandable?: boolean;
  expandableChild?: (row: Row<any>) => ReactNode;
  hideFooter?: boolean;
  containerClassName?: string;
  hideScrollbar?: boolean;
  fill?: boolean;

  actions?: ReactNode;
  selectionActions?: ReactNode;

  externalSelection?: TableSelection;
  onSelectionChange?: (selection: TableSelection) => void;
}

// Memoized row component to prevent unnecessary re-renders
const VirtualizedRow = React.memo(
  ({
    row,
    virtualRow,
    rowVirtualizer,
    expandable,
    expandableChild,
    columnHeaders,
    containerWidth,
    onToggleExpanded,
    isSelected,
  }: {
    row: Row<any>;
    virtualRow: any;
    rowVirtualizer: any;
    expandable: boolean;
    expandableChild?: (row: Row<any>) => ReactNode;
    columnHeaders: any[];
    containerWidth: number;
    onToggleExpanded: (rowId: string) => void;
    isSelected: boolean;
    actions?: ReactNode;
    selectionActions?: ReactNode;
  }) => {
    const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null);
    const clickTimerRef = useRef<NodeJS.Timeout>(null);
    const dragThreshold = 5;

    const handleMouseDown = useCallback(
      (e: React.MouseEvent) => {
        if (!expandable) return;

        mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
        clickTimerRef.current = setTimeout(() => {
          mouseDownPosRef.current = null; // long press -> disable click
        }, 300);
      },
      [expandable],
    );

    const handleMouseUp = useCallback(
      (e: React.MouseEvent) => {
        if (!expandable) return;

        // Don't open the expanded if it is a button and not the row
        const target = e.target as HTMLElement;
        if (
          target.closest(
            "button, a, input, select, textarea, [role='button'], [role='menuitem']",
          )
        ) {
          return;
        }

        if (clickTimerRef.current) {
          clearTimeout(clickTimerRef.current);
        }

        if (!mouseDownPosRef.current) return;

        const dx = Math.abs(e.clientX - mouseDownPosRef.current.x);
        const dy = Math.abs(e.clientY - mouseDownPosRef.current.y);

        if (dx < dragThreshold && dy < dragThreshold) {
          onToggleExpanded(row.id);
        }

        mouseDownPosRef.current = null;
      },
      [expandable, row.id, onToggleExpanded],
    );

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (clickTimerRef.current) {
          clearTimeout(clickTimerRef.current);
        }
      };
    }, []);

    return (
      <React.Fragment key={row.id}>
        <TableRow
          data-index={virtualRow.index}
          data-state={isSelected && "selected"}
          ref={rowVirtualizer.measureElement}
          className={expandable ? "cursor-pointer" : ""}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        >
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>

        {expandable && row.getIsExpanded() && expandableChild && (
          <TableRow>
            <TableCell colSpan={columnHeaders.length} className="p-0 relative">
              <div
                className="sticky left-0 z-10 bg-background h-full w-4"
                style={{ width: containerWidth }}
              >
                <div className="max-w-[100%] text-wrap">
                  {expandableChild(row)}
                </div>
              </div>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  },
);

VirtualizedRow.displayName = "VirtualizedRow";

export default function DataTable({
  data,
  columns,
  pageSizes = [15, 25, 50, 100],
  kpis,
  searchbar = false,
  searchValue = "",
  setSearchValue = () => {},
  tableReady = () => {},
  expandable = false,
  expandableChild = undefined,
  selectable = false,
  hideFooter = false,
  containerClassName,
  hideScrollbar = true,
  fill = false,
  actions = null,
  selectionActions = null,
  externalSelection = undefined,
  onSelectionChange = undefined,
}: TableProps) {
  if (pageSizes.length === 0) {
    throw new Error("pageSizes must contain at least one value in table");
  }

  const { t } = useTranslation();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState(
    externalSelection || {},
  );
  const selectionAnchorRef = useRef<string | null>(null);
  const [pageSize, setPageSize] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSizes[0],
  });
  const [expanded, setExpanded] = React.useState({});
  const [containerWidth, setContainerWidth] = React.useState(0);
  const [hasSelection, setHasSelection] = React.useState(false);
  const cols = useMemo(() => columns, [columns]);

  const table = useReactTable({
    data,
    columns: cols,
    getCoreRowModel: getCoreRowModel(),
    ...(!hideFooter && {
      getPaginationRowModel: getPaginationRowModel(),
    }),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onPaginationChange: setPageSize,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    ...(expandable && {
      onExpandedChange: setExpanded,
      getSubRows: () => [],
    }),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: pageSize,
      ...(expandable && { expanded }),
    },
    meta: {
      selectionAnchorRef,
    },
  });

  useEffect(() => {
    if (externalSelection) setRowSelection(externalSelection);
  }, [externalSelection]);

  // Notify parent on selection change
  const prevSelectedIds = useRef<string[]>([]);
  useEffect(() => {
    if (!onSelectionChange) return;

    const currentSelectionKeys = Object.keys(table.getState().rowSelection);

    // Compare with previous selection
    const changed =
      currentSelectionKeys.length !== prevSelectedIds.current.length ||
      currentSelectionKeys.some(
        (key) => !prevSelectedIds.current.includes(key),
      );

    if (changed) {
      prevSelectedIds.current = currentSelectionKeys;
      onSelectionChange(table.getState().rowSelection);
    }
  }, [table.getState().rowSelection, onSelectionChange]);

  useEffect(() => {
    if (searchValue) table.setGlobalFilter(searchValue);
  }, [searchValue, table]);

  useEffect(() => {
    if (tableReady) tableReady(table);
  }, [table, tableReady]);

  // This ref is needed so we dont do unnecessary resets on filters that causes rerenders
  const prevFilters = useRef<ColumnFiltersState>([]);
  useEffect(() => {
    const filtersChanged =
      JSON.stringify(prevFilters.current) !== JSON.stringify(columnFilters);

    if (filtersChanged && Object.keys(rowSelection).length > 0) {
      setRowSelection({});
      onSelectionChange?.({});
    }

    if (filtersChanged) {
      selectionAnchorRef.current = null;
    }

    prevFilters.current = columnFilters;
  }, [columnFilters]);

  useEffect(() => {
    setHasSelection(Object.keys(rowSelection).length > 0);
  }, [rowSelection]);

  const updateWidth = useCallback(() => {
    if (parentRef.current) {
      setContainerWidth(parentRef.current.clientWidth - 15);
    }
  }, []);

  useEffect(() => {
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [updateWidth]);

  const parentRef = useRef<HTMLDivElement>(null);
  const rows = table.getRowModel().rows;

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 20,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();

  const columnHeaders = useMemo(
    () => table.getFlatHeaders().filter((h) => h.column.getIsVisible()),
    [table],
  );

  const handleToggleExpanded = useCallback(
    (rowId: string) => {
      const targetRow = rows.find((r) => r.id === rowId);
      if (targetRow) {
        // Reset all other expanded
        if (!targetRow.getIsExpanded()) table.setExpanded({});
        targetRow.toggleExpanded();
      }
    },
    [rows, table],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      table.setGlobalFilter(value);
      setSearchValue(value);
    },
    [table, setSearchValue],
  );

  const handlePageSizeChange = useCallback((v: string) => {
    setPageSize({ pageIndex: 0, pageSize: parseInt(v) });
  }, []);

  return (
    <div
      className={cn(
        "w-full max-w-[100%]",
        fill && "flex h-full flex-col",
      )}
    >
      {/* KPIs + Searchbar */}
      <div className="mb-2 flex justify-between items-end align-center">
        {kpis && kpis.length > 0 && (
          <div className="flex justify-start">
            {kpis.map((kpi, i) => (
              <div key={i} className="space-y-1">
                {kpi.icon && <div className="h-8 w-8">{kpi.icon}</div>}
                <span className="text-sm font-medium leading-none">
                  {kpi.title}:
                </span>
                <span className="ml-1 text-sm font-bold">{kpi.value}</span>
                {kpis.length - 1 !== i && (
                  <span className="mx-1 text-sm font-medium leading-none">
                    |
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {searchbar && (
          <div>
            <input
              id="custom-search-bar"
              type="text"
              placeholder={t("common.search")}
              className="rounded-md border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
              onChange={handleSearchChange}
              value={table.getState().globalFilter ?? ""}
            />
          </div>
        )}
        {actions && (
          <div className="overflow-x-scroll no-scrollbar">
            {actions}
            {hasSelection && selectionActions}
          </div>
        )}
      </div>

      {/* Table */}
      <div
        ref={parentRef}
        className={cn(
          "rounded-md border overflow-auto bg-muted",
          fill ? "min-h-0 flex-1" : "max-h-[80vh]",
          hideScrollbar && "no-scrollbar",
          containerClassName,
        )}
      >
        <Table>
          <colgroup>
            {columnHeaders.map((header) => (
              <col key={header.id} style={{ width: header.getSize() }} />
            ))}
          </colgroup>

          <TableHeader className="bg-background sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {virtualRows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {t("common.noData")}
                </TableCell>
              </TableRow>
            )}
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index];
              return (
                <VirtualizedRow
                  key={row.id}
                  row={row}
                  isSelected={selectable && row.getIsSelected()}
                  virtualRow={virtualRow}
                  rowVirtualizer={rowVirtualizer}
                  expandable={expandable}
                  expandableChild={expandableChild}
                  columnHeaders={columnHeaders}
                  containerWidth={containerWidth}
                  onToggleExpanded={handleToggleExpanded}
                />
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Footer */}
      {!hideFooter && (
        <div className="flex items-center justify-end space-x-2 py-4">
          <div className="text-muted-foreground flex-1 text-sm">
            {selectable && (
              <span>
                {Object.keys(rowSelection).length +
                  " " +
                  t("common.of") +
                  " " +
                  table.getFilteredRowModel().rows.length +
                  " " +
                  t("table.selectedRows")}
              </span>
            )}
            {!selectable && <span>{rows.length + " " + t("table.rows")}</span>}
          </div>
          <div className="space-x-2">
            <Select
              onValueChange={handlePageSizeChange}
              defaultValue={pageSize.pageSize.toString()}
            >
              <SelectTrigger className="w-[100px]">
                <SelectValue placeholder={`Page Size: ${pageSize.pageSize}`} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Page Size</SelectLabel>
                  {pageSizes.map((size, i) => (
                    <SelectItem key={i} value={size.toString()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className="space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              type="button"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              type="button"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
